import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import CompanionFull from '@/components/companion/CompanionFull';
import { X, Search, AlertTriangle } from 'lucide-react';

const TIERS = ['all', 'bronze', 'silver', 'gold', 'platinum', 'free_comp'];

/** Photos only. No names on the grid — a practitioner clicks to learn who someone is. */
const ParticipantTile = ({ participant, onOpen }) => (
  <button
    onClick={() => onOpen(participant)}
    className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10
               bg-white/[0.03] transition-all duration-200 hover:border-cyan-300/60
               hover:shadow-[0_0_24px_rgba(103,232,249,0.18)]"
    data-testid={`participant-tile-${participant.user_id}`}
    aria-label="Open this participant's Consciousness Progression"
  >
    {participant.photo ? (
      <img
        src={participant.photo}
        alt=""
        className="h-full w-full object-cover transition-transform duration-300
                   group-hover:scale-105"
      />
    ) : (
      <span className="flex h-full w-full items-center justify-center text-2xl font-semibold
                       tracking-wider text-white/40">
        {participant.initials}
      </span>
    )}
    {participant.needs_attention && (
      <span
        className="absolute right-2 top-2 rounded-full bg-amber-400/90 p-1"
        title={participant.needs_attention.reason}
        data-testid={`participant-attention-${participant.user_id}`}
      >
        <AlertTriangle className="h-3 w-3 text-black" />
      </span>
    )}
  </button>
);

const ParticipantIndexPage = ({ embedded = false }) => {
  const [index, setIndex] = useState(null);
  const [error, setError] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [search, setSearch] = useState('');
  const [tier, setTier] = useState('all');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [companion, setCompanion] = useState(null);
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('companion-modal-open', Boolean(openId));
    return () => document.body.classList.remove('companion-modal-open');
  }, [openId]);

  // Filters and paging live on the server so a search still reaches every member,
  // not just the fifty currently on screen.
  useEffect(() => {
    const params = new URLSearchParams({ page: String(page), per_page: '50' });
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (tier !== 'all') params.set('tier', tier);
    if (attentionOnly) params.set('attention_only', 'true');
    if (selectedModule !== 'all') params.set('module_order', String(selectedModule));
    setLoading(true);
    axios.get(`${API}/practitioner/participants?${params.toString()}`)
      .then((res) => setIndex(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Could not load participants'))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch, tier, attentionOnly, selectedModule]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, tier, attentionOnly, selectedModule]);

  const loadCompanion = useCallback((participantId) => {
    setCompanion(null);
    setOpenId(participantId);
    axios.get(`${API}/practitioner/participants/${participantId}/companion`)
      .then((res) => setCompanion(res.data))
      .catch(() => setCompanion('error'));
  }, []);

  const groups = useMemo(
    () => (index ? index.groups.filter((g) => g.participants.length > 0) : []),
    [index]
  );

  if (error) {
    return (
      <div className="text-center py-20 text-white/60" data-testid="participants-error">{error}</div>
    );
  }
  if (!index) {
    return <div className="text-center py-20 text-white/60">Loading participants…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto pb-16" data-testid="participants-page">
      <header className="mb-8">
        {embedded ? (
          <h2 className="text-base md:text-lg font-semibold text-white">Participants</h2>
        ) : (
          <h1 className="text-4xl sm:text-5xl font-bold text-white">Practitioner Dashboard</h1>
        )}
        <p className="text-base md:text-lg text-white/60 mt-2">
          All participants in the coaching program — {index.total} people. Tap a face to open their
          Consciousness Progression.
          {index.filtered_total !== index.total && (
            <span data-testid="participants-filtered-count">
              {' '}Showing {index.filtered_total} that match your filters.
            </span>
          )}
        </p>
      </header>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-2" data-testid="module-selector">
        {[{ order: 'all', short_label: 'All' }, ...index.groups].map((g) => (
          <button
            key={String(g.order)}
            onClick={() => setSelectedModule(g.order)}
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
              String(selectedModule) === String(g.order)
                ? 'border-cyan-300/60 bg-cyan-300/15 text-cyan-100'
                : 'border-white/10 text-white/60 hover:border-white/30'
            }`}
            data-testid={`module-btn-${g.order}`}
          >
            {g.short_label}
            {g.count !== undefined && g.count > 0 && (
              <span className="ml-2 text-xs text-white/40">{g.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="participant-filters">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            className="rounded-full border border-white/10 bg-white/[0.03] py-1.5 pl-9 pr-4 text-sm
                       text-white placeholder-white/30 focus:outline-none focus:border-cyan-300/60"
            data-testid="participant-search"
          />
        </div>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="rounded-full border border-white/10 bg-[#0b0e20] px-4 py-1.5 text-sm text-white/80
                     focus:outline-none focus:border-cyan-300/60"
          data-testid="participant-tier-filter"
        >
          {TIERS.map((t) => (
            <option key={t} value={t}>{t === 'all' ? 'All tiers' : t}</option>
          ))}
        </select>
        <button
          onClick={() => setAttentionOnly((v) => !v)}
          className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
            attentionOnly
              ? 'border-amber-300/60 bg-amber-300/15 text-amber-100'
              : 'border-white/10 text-white/60 hover:border-white/30'
          }`}
          data-testid="participant-attention-filter"
        >
          Needs attention
          <span className="ml-2 text-xs text-white/40">{index.needs_attention_count}</span>
        </button>
        <span className="text-xs text-white/30">
          No assessment submitted within {index.needs_attention_days} days of reaching their
          current module
        </span>
      </div>

      <div className="space-y-10">
        {groups.map((group) => (
          <section key={String(group.order)} data-testid={`module-section-${group.order}`}>
            <div className="mb-4 flex items-baseline gap-3 border-b border-white/10 pb-2">
              <h2 className="text-base md:text-lg font-semibold text-white">{group.title}</h2>
              <span className="text-xs text-white/40">{group.participants.length}</span>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
              {group.participants.map((p) => (
                <ParticipantTile key={p.user_id} participant={p} onOpen={() => loadCompanion(p.user_id)} />
              ))}
            </div>
          </section>
        ))}
        {groups.length === 0 && !loading && (
          <p className="text-sm text-white/50" data-testid="participants-empty">
            No participants match those filters.
          </p>
        )}
      </div>

      {index.total_pages > 1 && (
        <div
          className="mt-12 flex items-center justify-between border-t border-white/10 pt-5"
          data-testid="participants-pager"
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={index.page <= 1 || loading}
            className="rounded-full border border-white/15 px-5 py-1.5 text-sm text-white/70
                       transition-colors duration-200 hover:border-white/40 disabled:opacity-30"
            data-testid="participants-prev-page"
          >
            Previous
          </button>
          <span className="text-xs text-white/40" data-testid="participants-page-label">
            Page {index.page} of {index.total_pages} · {index.showing} of {index.filtered_total} shown
          </span>
          <button
            onClick={() => setPage((p) => Math.min(index.total_pages, p + 1))}
            disabled={index.page >= index.total_pages || loading}
            className="rounded-full border border-white/15 px-5 py-1.5 text-sm text-white/70
                       transition-colors duration-200 hover:border-white/40 disabled:opacity-30"
            data-testid="participants-next-page"
          >
            Next
          </button>
        </div>
      )}

      {openId && (
        <div
          className="fixed inset-0 z-[70] overflow-y-auto bg-[#05070f]/[0.99] backdrop-blur-3xl"
          data-testid="companion-modal"
        >
          <div className="mx-auto max-w-4xl px-6 py-10">
            <button
              onClick={() => { setOpenId(null); setCompanion(null); }}
              className="mb-6 flex items-center gap-2 text-sm text-white/60 hover:text-white
                         transition-colors duration-200"
              data-testid="companion-modal-close"
            >
              <X className="h-4 w-4" /> Close
            </button>
            {companion === 'error' && (
              <p className="text-white/60">That Companion could not be loaded.</p>
            )}
            {!companion && <p className="text-white/60">Opening their Companion…</p>}
            {companion && companion !== 'error' && (
              <CompanionFull data={companion} onReload={() => loadCompanion(openId)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantIndexPage;
